import { useUserDirectory } from "@/hooks/use-user-directory";
import {
  ActionConfirmationModal,
  AppModal,
  Button,
  DateFormField,
  Form,
  FormSubmitButton,
  SearchableSelectFormField,
  SelectFormField,
  SwitchFormField,
  TextAreaFormField,
} from "@/components/ui";
import { TimeFormField } from "@/components/ui/FormFields/TimeFormField/TimeFormField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import {
  useCreateMatterUserConnection,
  useDeleteMatterUserConnection,
} from "@/lib/data/react-query";
import {
  useCreateMatterLitigationDate,
  useDeleteMatterLitigationDate,
  useUpdateMatterLitigationDate,
} from "@/lib/data/react-query/matters/matter-litigation-date";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import {
  MATTER_LITIGATION_DATE_RESPONSIBLE_FIELD_LABEL,
  MATTER_LITIGATION_DATE_USER_CONNECTION_LABEL,
} from "@/lib/data/static/matters/litigationStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Matter, MatterLitigationDate } from "@/lib/types";
import { formatUsersName } from "@/lib/utils/format";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { cn } from "@/lib/utils/cn";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { mergeInitialData } from "@/lib/utils/initialDataUtils";

interface AddOrEditMatterLitigationDateModalProps {
  title: string;
  description?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  editing?: boolean;
  matterId: string;
  litigationDateId?: string;
  initialData?: Partial<MatterLitigationDate>;
}

const litigationDateFormSchema = z.object({
  typeId: z.string().min(1, "Type is required"),
  deadlineDate: z.string().min(1, "Deadline date is required"),
  deadlineTime: z.string().optional(),
  completedDate: z.string().optional(),
  confirmation: z.boolean().optional().default(false),
  description: z.string().optional(),
  responsibleUserId: z.string().optional(),
});

const defaultFormValues: z.infer<typeof litigationDateFormSchema> = {
  typeId: "",
  deadlineDate: "",
  deadlineTime: "",
  completedDate: "",
  confirmation: false,
  description: "",
  responsibleUserId: "",
};

type LitigationDatePayload = {
  typeId: string;
  deadlineDate: string;
  deadlineTime?: string;
  completedDate?: string;
  description?: string;
  confirmation: boolean;
  planId?: string;
};

const sanitizePayload = (
  values: z.infer<typeof litigationDateFormSchema>,
): LitigationDatePayload => {
  return {
    typeId: values.typeId,
    deadlineDate: values.deadlineDate,
    deadlineTime: values.deadlineTime?.trim() || undefined,
    // Preserve empty string so clearing the date persists
    completedDate:
      values.completedDate === ""
        ? ""
        : values.completedDate?.trim() || undefined,
    description: values.description?.trim() || undefined,
    confirmation: values.confirmation,
  };
};

const litigationDateModalFormId = "litigation-date-modal-form";

export const AddOrEditMatterLitigationDateModal = ({
  title,
  description,
  open,
  setOpen,
  editing,
  matterId,
  litigationDateId,
  initialData,
}: AddOrEditMatterLitigationDateModalProps) => {
  const { hasActionPermission } = useUser();

  const canManageResponsibleUser = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterLitigationDate,
    editing ? ACTION_PERMISSION_VALUES.update : ACTION_PERMISSION_VALUES.create,
  );

  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );
  const matter = matterData.data;
  const activePlanId =
    (matter as Partial<Matter>).litigationDatesPlanConnection?.[0]
      ?.matterLitigationDatePlanId ?? "";

  const hasSettingsPermission = true;

  const matterSettingsQueryOptions = retrieveMatterSettingsQueryOptions();
  const { data: matterSettingsData, isLoading: isMatterSettingsLoading } =
    useQuery({
      ...matterSettingsQueryOptions,
      enabled: hasSettingsPermission,
    });
  const litigationDateTypes = useMemo(() => {
    return matterSettingsData?.data.litigationDateTypes ?? [];
  }, [matterSettingsData]);

  const activeLitigationDate = useMemo(() => {
    return matter.litigationDates.find((date) => date.id === litigationDateId);
  }, [litigationDateId, matter.litigationDates]);

  const currentLitigationDateUserConnection = useMemo(() => {
    if (!activeLitigationDate) return undefined;
    return matter.userConnections.find(
      (connection) =>
        connection.litigationDateId === activeLitigationDate.id &&
        connection.label === MATTER_LITIGATION_DATE_USER_CONNECTION_LABEL,
    );
  }, [activeLitigationDate, matter.userConnections]);

  const litigationDateInitialValues = mergeInitialData(
    activeLitigationDate,
    initialData,
  );

  const hasEditPermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterLitigationDate,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasCreatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterLitigationDate,
    ACTION_PERMISSION_VALUES.create,
  );
  const hasDeletePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterLitigationDate,
    ACTION_PERMISSION_VALUES.delete,
  );

  const isReadOnly =
    (!editing && !hasCreatePermission) || (editing && !hasEditPermission);
  const canSubmit = editing ? hasEditPermission : hasCreatePermission;

  const formValues = useMemo(() => {
    if (editing && litigationDateInitialValues) {
      const typeId = litigationDateInitialValues.typeId;
      const typeExists = litigationDateTypes.some((type) => type.id === typeId);

      return {
        typeId: !isMatterSettingsLoading && typeExists ? typeId : "",
        deadlineDate: litigationDateInitialValues.deadlineDate ?? "",
        deadlineTime: litigationDateInitialValues.deadlineTime ?? "",
        completedDate: litigationDateInitialValues.completedDate ?? "",
        confirmation: litigationDateInitialValues.confirmation ?? false,
        description: litigationDateInitialValues.description ?? "",
        responsibleUserId: currentLitigationDateUserConnection?.userId ?? "",
      };
    }
    return defaultFormValues;
  }, [
    currentLitigationDateUserConnection?.userId,
    editing,
    litigationDateInitialValues,
    litigationDateTypes,
    isMatterSettingsLoading,
  ]);

  const form = useForm<z.infer<typeof litigationDateFormSchema>>({
    resolver: zodResolver(litigationDateFormSchema),
    values: formValues,
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });
  const userDirectory = useUserDirectory({
    enabled: open,
    selectedIds: [form.watch("responsibleUserId") ?? ""],
  });
  const {
    users,
    isLoading: isUsersLoading,
    isError: isUsersError,
  } = userDirectory;

  const createLitigationDate = useCreateMatterLitigationDate();
  const updateLitigationDate = useUpdateMatterLitigationDate();
  const deleteLitigationDate = useDeleteMatterLitigationDate();
  const createMatterUserConnection = useCreateMatterUserConnection({
    showToast: false,
  });
  const deleteMatterUserConnection = useDeleteMatterUserConnection({
    showToast: false,
  });

  const [isDeleting, setIsDeleting] = useState(false);

  const isLoading =
    createLitigationDate.isPending ||
    updateLitigationDate.isPending ||
    deleteLitigationDate.isPending ||
    createMatterUserConnection.isPending ||
    deleteMatterUserConnection.isPending;

  useEffect(() => {
    if (!open) {
      form.reset(formValues);
    }
  }, [activeLitigationDate, editing, form, open, formValues]);

  const handleSubmit = async (
    values: z.infer<typeof litigationDateFormSchema>,
  ) => {
    if (editing && !hasEditPermission) return;
    if (!editing && !hasCreatePermission) return;

    const selectedUserId = values.responsibleUserId?.trim() ?? "";
    const payload = sanitizePayload(values);
    if (!editing && activePlanId) {
      payload.planId = activePlanId;
    }

    if (editing && activeLitigationDate) {
      await updateLitigationDate.mutateAsync({
        matterId,
        id: activeLitigationDate.id,
        dateModified: activeLitigationDate.dateModified,
        ...payload,
      });

      if (canManageResponsibleUser) {
        const userChanged =
          selectedUserId !==
          (currentLitigationDateUserConnection?.userId ?? "");
        if (userChanged) {
          if (currentLitigationDateUserConnection) {
            await deleteMatterUserConnection.mutateAsync({
              matterId,
              id: currentLitigationDateUserConnection.id,
            });
          }
          if (selectedUserId) {
            const selectedUser = users.find(
              (user) => user.id === selectedUserId,
            );
            if (selectedUser) {
              await createMatterUserConnection.mutateAsync({
                matterId,
                litigationDateId: activeLitigationDate.id,
                label: MATTER_LITIGATION_DATE_USER_CONNECTION_LABEL,
                userId: selectedUser.id,
              });
            }
          }
        }
      }
    } else {
      const created = await createLitigationDate.mutateAsync({
        matterId,
        ...payload,
      });
      if (canManageResponsibleUser && selectedUserId && created.data.id) {
        const selectedUser = users.find((user) => user.id === selectedUserId);
        if (selectedUser) {
          await createMatterUserConnection.mutateAsync({
            matterId,
            litigationDateId: created.data.id,
            label: MATTER_LITIGATION_DATE_USER_CONNECTION_LABEL,
            userId: selectedUser.id,
          });
        }
      }
    }
    setOpen(false);
  };

  const handleDelete = async () => {
    if (!activeLitigationDate || !hasDeletePermission) return;

    await deleteLitigationDate.mutateAsync({
      id: activeLitigationDate.id,
      matterId,
    });
    setIsDeleting(false);
    setOpen(false);
  };

  const renderBodyContent = (fieldsetClassName: string) => (
    <fieldset className={fieldsetClassName} disabled={isReadOnly}>
      <SelectFormField
        items={litigationDateTypes}
        itemsKey="id"
        formatItemLabel={(item) => item?.label}
        register={form.register("typeId")}
        label="Litigation date type*"
        placeholder="Select a type"
        isItemsLoading={isMatterSettingsLoading}
      />
      <DateFormField
        register={form.register("deadlineDate")}
        name="deadlineDate"
        label="Deadline date*"
      />
      <TimeFormField
        register={form.register("deadlineTime")}
        name="deadlineTime"
        label="Deadline time"
      />
      <DateFormField
        register={form.register("completedDate")}
        name="completedDate"
        label="Completed date"
      />
      <div className="col-span-2 flex items-center">
        <SwitchFormField
          register={form.register("confirmation")}
          label="Confirmed"
          disabled={isReadOnly}
        />
      </div>
      <div className="col-span-2">
        <TextAreaFormField
          register={form.register("description")}
          label="Description"
          textAreaClassName="field-sizing-content max-h-[20lh]"
        />
      </div>
      <div className="col-span-2">
        <SearchableSelectFormField
          items={users}
          {...userDirectory.selectProps}
          itemsKey="id"
          formatItemLabel={(user) => formatUsersName(user) ?? ""}
          formatSelectedItemLabel={(user) => formatUsersName(user) ?? ""}
          placeholder="Select responsible team member"
          label={MATTER_LITIGATION_DATE_RESPONSIBLE_FIELD_LABEL}
          register={form.register("responsibleUserId")}
          isItemsLoading={isUsersLoading}
          isItemsError={isUsersError}
          disabled={isReadOnly || !canManageResponsibleUser || isLoading}
          clearable
        />
      </div>
    </fieldset>
  );

  return (
    <>
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={title}
          description={description}
          size="2xl"
          loading={isLoading}
          isDirty={form.formState.isDirty}
          bodyFormId={litigationDateModalFormId}
          bodyFormOnSubmit={form.handleSubmit(handleSubmit)}
          footer={
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                {editing && hasDeletePermission && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => {
                      setIsDeleting(true);
                    }}
                  >
                    Delete
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <FormCancelButton
                  label={isReadOnly ? "Close" : "Cancel"}
                  setOpen={setOpen}
                  isDirty={form.formState.isDirty}
                />
                <FormSubmitButton
                  submitFormId={litigationDateModalFormId}
                  title={editing ? "Save" : "Add litigation date"}
                  loading={isLoading}
                  disabled={!canSubmit}
                  overrideDisabled={Boolean(initialData)}
                />
              </div>
            </div>
          }
        >
          {renderBodyContent(
            cn(slideoverFieldsetClassName, "my-0 px-0 pt-0 pb-4"),
          )}
        </AppModal>
      </Form>

      <ActionConfirmationModal
        title="Are you sure you want to delete this litigation date?"
        open={isDeleting}
        setOpen={setIsDeleting}
        afterActionFn={handleDelete}
      />
    </>
  );
};
